import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check, ChevronDown } from "lucide-react";
import { LANGS } from "@/i18n";

/**
 * Dropdown switcher with flags. Place inside header / topbar.
 * Props:
 *   compact: when true, shows only globe icon (mobile)
 */
export default function LanguageSwitcher({ compact = false }) {
    const { i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    const current = LANGS.find((l) => l.code === i18n.language) || LANGS[0];

    useEffect(() => {
        if (!open) return;
        const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        window.addEventListener("click", onClick);
        return () => window.removeEventListener("click", onClick);
    }, [open]);

    const switchTo = (code) => {
        i18n.changeLanguage(code);
        setOpen(false);
    };

    return (
        <div ref={ref} className="relative" data-testid="language-switcher">
            <button
                onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
                data-testid="language-switcher-button"
                aria-label="Select language"
                className={`inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 hover:bg-slate-50 transition-colors text-sm font-semibold ${
                    compact ? "h-9 w-9 justify-center" : "px-3 py-1.5"
                }`}
            >
                {compact ? (
                    <Globe size={16} className="text-slate-700" />
                ) : (
                    <>
                        <span className="text-base leading-none">{current.flag}</span>
                        <span className="hidden md:inline">{current.code.toUpperCase()}</span>
                        <ChevronDown size={14} className="text-slate-400" />
                    </>
                )}
            </button>
            {open && (
                <div
                    className="absolute right-0 mt-2 w-48 bg-white border border-slate-200 rounded-xl shadow-xl py-1 z-50 max-h-96 overflow-y-auto"
                    data-testid="language-switcher-menu"
                    onClick={(e) => e.stopPropagation()}
                >
                    {LANGS.map((l) => (
                        <button
                            key={l.code}
                            onClick={() => switchTo(l.code)}
                            data-testid={`language-option-${l.code}`}
                            className={`w-full px-3 py-2 text-left text-sm flex items-center gap-3 hover:bg-slate-50 ${
                                i18n.language === l.code ? "text-red-700 font-semibold" : "text-slate-700"
                            }`}
                        >
                            <span className="text-lg leading-none">{l.flag}</span>
                            <span className="flex-1">{l.name}</span>
                            {i18n.language === l.code && <Check size={14} className="text-red-600" />}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
